
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    Form,
    Input,
    Button,
    Select,
    Tabs,
    Space,
    Typography,
    Alert,
    Modal,
    Row,
    Col,
    Divider
} from 'antd';
import {
    UserOutlined,
    LockOutlined,
    MailOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    EnvironmentOutlined
} from '@ant-design/icons';
import { ENV, IS_DOCKER, SERVER_CONFIG, getServerOptions } from "../../shared/utils/enum";
import toast from "react-hot-toast";
import { defaultLogin, defaultRegister } from "../../shared/utils/defaultData";
import PasswordRecovery from "../passwordRecovery/PasswordRecovery";
import { useLogin, registerRequest } from "../../shared/hooks/useAuth";
import "./login.scss";

const { Title, Text, Link } = Typography;
// TabPane은 deprecated되어 items 방식 사용

const Login = () => {
    const [activeTab, setActiveTab] = useState('login');
    const [err, setErr] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loginForm] = Form.useForm();
    const [registerForm] = Form.useForm();

    const navigate = useNavigate();
    const { mutate: login, isPending } = useLogin();
    const channel = new BroadcastChannel('environment-settings');


    const handleLogin = async (values) => {
        try {
            const loginData = { ...values };
            login(loginData, {
                onSuccess: (user) => {
                    navigate("/")
                    channel.postMessage({user});
                },
                onError: (error) => {
                    const errorMessage = error.response?.data?.message || "로그인에 실패하였습니다.";
                    setErr(errorMessage);
                }
            });
        } catch (err) {
            setErr(err.message || "로그인 중 오류가 발생했습니다.");
        }
    };

    const handleRegister = async (values) => {
        try {
            const result = await registerRequest(values);
            if (result) {
                toast.success("회원가입에 성공하였습니다!");
                setActiveTab('login');
                registerForm.resetFields();
            }
        } catch (error) {
            toast.error("회원가입에 실패하였습니다.");
        }
    };

    const showPasswordModal = () => {
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setErr(null);
        loginForm.resetFields();
        registerForm.resetFields();
    };

    return (
        <div className="login-container">
            <Row justify="center" align="middle" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Col xs={22} sm={16} md={12} lg={8} xl={6}>
                    <Card
                        className="login-card"
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                            border: 'none'
                        }}
                    >
                        {/* 로고 섹션 */}
                        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                            <div style={{
                                width: '80px',
                                height: '80px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '50%',
                                margin: '0 auto 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <UserOutlined style={{ fontSize: '32px', color: '#fff' }} />
                            </div>
                            <Title level={2} style={{ margin: 0, color: '#f3d2db' }}>
                                Kitty Friends
                            </Title>
                            <Text type="secondary">관리자 대시보드</Text>
                        </div>

                        {/* 에러 메시지 */}
                        {err && (
                            <Alert
                                message={err}
                                type="error"
                                showIcon
                                closable
                                onClose={() => setErr(null)}
                                style={{ marginBottom: '24px' }}
                            />
                        )}

                        {/* 탭 컨테이너 */}
                        <Tabs
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            centered
                            size="large"
                            items={[
                                {
                                    key: 'login',
                                    label: '로그인',
                                    children: (
                                <Form
                                    form={loginForm}
                                    name="login"
                                    onFinish={handleLogin}
                                    layout="vertical"
                                    size="large"
                                    requiredMark={false}
                                >
                                    <Form.Item
                                        name="id"
                                        rules={[
                                            { required: true, message: '아이디를 입력해주세요!' },
                                            { min: 3, message: '아이디는 최소 3자 이상이어야 합니다!' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="아이디를 입력하세요"
                                            autoComplete="username"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="password"
                                        rules={[
                                            { required: true, message: '비밀번호를 입력해주세요!' },
                                            { min: 4, message: '비밀번호는 최소 4자 이상이어야 합니다!' }
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="비밀번호를 입력하세요"
                                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                            autoComplete="current-password"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="env"
                                        label="환경 선택"
                                        initialValue={IS_DOCKER ? ENV.DOCKER : ENV.DEVELOP}
                                        rules={[{ required: true, message: '환경을 선택해주세요!' }]}
                                    >
                                        <Select
                                            placeholder="환경을 선택하세요"
                                            suffixIcon={<EnvironmentOutlined />}
                                        >
                                            {getServerOptions({ includeProduction: false }).map(env => {
                                                const config = SERVER_CONFIG[env];
                                                return (
                                                    <Select.Option key={env} value={env}>
                                                        <Space>
                                                            <span style={{ color: config.color }}>●</span>
                                                            {config.fullLabel}
                                                        </Space>
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: '16px' }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={isPending}
                                            block
                                            size="large"
                                            style={{
                                                height: '48px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            {isPending ? '로그인 중...' : '로그인'}
                                        </Button>
                                    </Form.Item>

                                    <div style={{ textAlign: 'center' }}>
                                        <Link onClick={showPasswordModal}>
                                            비밀번호를 잊으셨나요?
                                        </Link>
                                    </div>
                                </Form>
                                    )
                                },
                                {
                                    key: 'register',
                                    label: '회원가입',
                                    children: (
                                <Form
                                    form={registerForm}
                                    name="register"
                                    onFinish={handleRegister}
                                    layout="vertical"
                                    size="large"
                                    requiredMark={false}
                                >
                                    <Form.Item
                                        name="id"
                                        rules={[
                                            { required: true, message: '아이디를 입력해주세요!' },
                                            { min: 3, message: '아이디는 최소 3자 이상이어야 합니다!' },
                                            { pattern: /^[a-zA-Z0-9_]+$/, message: '아이디는 영문, 숫자, 언더스코어만 사용 가능합니다!' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="아이디를 입력하세요"
                                            autoComplete="username"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="password"
                                        rules={[
                                            { required: true, message: '비밀번호를 입력해주세요!' },
                                            { min: 8, message: '비밀번호는 최소 8자 이상이어야 합니다!' },
                                            {
                                                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                                                message: '비밀번호는 대소문자, 숫자, 특수문자를 포함해야 합니다!'
                                            }
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="비밀번호를 입력하세요"
                                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                            autoComplete="new-password"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="confirmPassword"
                                        dependencies={['password']}
                                        rules={[
                                            { required: true, message: '비밀번호를 다시 입력해주세요!' },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('비밀번호가 일치하지 않습니다!'));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password
                                            prefix={<LockOutlined />}
                                            placeholder="비밀번호를 다시 입력하세요"
                                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                            autoComplete="new-password"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="name"
                                        rules={[
                                            { required: true, message: '이름을 입력해주세요!' },
                                            { min: 2, message: '이름은 최소 2자 이상이어야 합니다!' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<UserOutlined />}
                                            placeholder="이름을 입력하세요"
                                            autoComplete="name"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="email"
                                        rules={[
                                            { required: true, message: '이메일을 입력해주세요!' },
                                            { type: 'email', message: '올바른 이메일 형식이 아닙니다!' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<MailOutlined />}
                                            placeholder="이메일을 입력하세요"
                                            autoComplete="email"
                                        />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={isPending}
                                            block
                                            size="large"
                                            style={{
                                                height: '48px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                border: 'none',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            {isPending ? '회원가입 중...' : '회원가입'}
                                        </Button>
                                    </Form.Item>
                                </Form>
                                    )
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 비밀번호 복구 모달 */}
            <Modal
                title="비밀번호 복구"
                open={isModalVisible}
                onCancel={handleModalCancel}
                footer={null}
                width={400}
                centered
                destroyOnClose
            >
                <PasswordRecovery reset={isModalVisible} onCancel={handleModalCancel} />
            </Modal>
        </div>
    );
};

export default Login;